import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { getMessaging, getToken } from 'firebase/messaging';
import { getApp } from 'firebase/app';
import { collection, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import type { CaseRecord } from '../types';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;

function todayDateKey(): string {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

let nativeTokenCache: string | null = null;
let nativeListenersInitialized = false;

/**
 * Initializes native push notification channels and listeners on Android.
 */
export async function initNativePushNotifications(
  onNotificationAction?: (data: Record<string, unknown>) => void
): Promise<void> {
  if (!Capacitor.isNativePlatform() || nativeListenersInitialized) return;
  nativeListenersInitialized = true;

  try {
    // Create high-importance notification channel for Android 8.0+
    await PushNotifications.createChannel({
      id: 'case_alerts',
      name: 'Case Alerts',
      description: 'Real-time alerts when your court hearing is coming up',
      importance: 5, // High importance (heads-up notification)
      visibility: 1,
      sound: 'default',
      vibration: true,
    });

    // Listen for notification taps when app is in background or closed
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data;
      if (data && onNotificationAction) {
        onNotificationAction(data);
      }
    });
  } catch (err) {
    console.warn('[PushNotifications] channel or listener setup failed:', err);
  }
}

/**
 * Fetches the FCM device token, either natively via Capacitor or via Web Push API.
 */
export async function getFcmToken(): Promise<string | null> {
  // 1. Native Android flow (Capacitor)
  if (Capacitor.isNativePlatform()) {
    try {
      if (nativeTokenCache) return nativeTokenCache;

      const permStatus = await PushNotifications.checkPermissions();
      let receive = permStatus.receive;
      if (receive !== 'granted') {
        const req = await PushNotifications.requestPermissions();
        receive = req.receive;
      }
      if (receive !== 'granted') return null;

      await initNativePushNotifications();

      return await new Promise<string | null>((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('[FCM] Native registration timed out');
          resolve(null);
        }, 10000);

        PushNotifications.addListener('registration', (token) => {
          clearTimeout(timeout);
          nativeTokenCache = token.value;
          resolve(token.value);
        }).then((handle) => {
          setTimeout(() => handle.remove(), 12000);
        });

        PushNotifications.addListener('registrationError', (err) => {
          clearTimeout(timeout);
          console.error('[FCM] Native registration error:', err);
          resolve(null);
        }).then((handle) => {
          setTimeout(() => handle.remove(), 12000);
        });

        PushNotifications.register().catch((err) => {
          clearTimeout(timeout);
          console.error('[FCM] PushNotifications.register failed:', err);
          resolve(null);
        });
      });
    } catch (err) {
      console.error('[FCM] Native getToken failed:', err);
      return null;
    }
  }

  // 2. Web browser flow (Firebase Web Messaging)
  if (!('Notification' in window) || !VAPID_KEY) return null;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
    const swReg = await navigator.serviceWorker.ready;
    const messaging = getMessaging(getApp());
    return await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg });
  } catch (err) {
    console.error('[FCM] Web getToken failed:', err);
    return null;
  }
}

export async function watchCase(c: CaseRecord, token: string): Promise<string> {
  const ref = await addDoc(collection(db, 'caseWatchers'), {
    token,
    caseNumber: c.caseNumber,
    location: c.location,
    court: c.court,
    itemNumber: c.itemNumber,
    dateKey: todayDateKey(),
    notified: false,
    createdAt: Date.now(),
  });
  return ref.id;
}

export async function unwatchCase(watchId: string): Promise<void> {
  await deleteDoc(doc(db, 'caseWatchers', watchId));
}
